/**
 * Вкладка «Рік»: Помісячно, Безбитковість і DSCR, Кредит, Параметри.
 *
 * Таблиці транспоновані там, де так зроблено в еталонній формі: у «Помісячно»
 * показники стоять рядками, місяці — стовпцями (§7 дизайн-контракту). Перша
 * колонка sticky, бо 12 місяців плюс підсумок не вміщаються в екран.
 *
 * Жодних вкладених рамок: лінії тільки між рядками, шапка — на --surface-2.
 */
import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';
import type {
  MonthRow, LoanRow, ParamRow, OpexRow, ScenarioYearRow, DscrRow, YearTotals,
} from '../types';
import { mln, n0, n2, pct, monthShort } from '../types';

const TH: React.CSSProperties = {
  background: 'var(--surface-2)', color: 'var(--text-muted)',
  fontSize: 11, fontWeight: 600, padding: '7px 10px', whiteSpace: 'nowrap',
  textAlign: 'right',
};
const TD: React.CSSProperties = {
  fontSize: 12, padding: '6px 10px', whiteSpace: 'nowrap', textAlign: 'right',
  borderTop: '1px solid var(--line)', color: 'var(--text)',
};
/** Перша колонка: прилипає при горизонтальній прокрутці. */
const STICKY: React.CSSProperties = {
  position: 'sticky', left: 0, zIndex: 1, textAlign: 'left',
  background: 'var(--surface-2)',
};

function Table({ children, minWidth }: { children: ReactNode; minWidth?: number }) {
  return (
    <div className="overflow-x-auto -mx-4 px-4">
      <table style={{ borderCollapse: 'collapse', minWidth, width: '100%' }}>{children}</table>
    </div>
  );
}

/** Рядок транспонованої таблиці: підпис + 12 місяців + підсумок року. */
function MetricRow({ label, months, pick, total, strong, dim }: {
  label: string;
  months: MonthRow[];
  pick: (m: MonthRow) => string;
  total: string;
  /** Підсумковий показник — напівжирним, щоб око ловило EBITDA й виручку. */
  strong?: boolean;
  /** Складова частина (в т.ч. …) — дрібніша й приглушена. */
  dim?: boolean;
}) {
  const w = strong ? 600 : 400;
  const color = dim ? 'var(--text-muted)' : 'var(--text)';
  return (
    <tr>
      <td style={{ ...TD, ...STICKY, fontWeight: w, color, fontSize: dim ? 11.5 : 12, paddingLeft: dim ? 22 : 10 }}>
        {label}
      </td>
      {months.map((m) => (
        <td key={m.month} style={{ ...TD, fontWeight: w, color }}>{pick(m)}</td>
      ))}
      <td style={{ ...TD, fontWeight: 600, color, background: 'var(--surface-2)' }}>{total}</td>
    </tr>
  );
}

export function MonthlyTable({ months, year }: { months: MonthRow[]; year: YearTotals }) {
  const { t } = useTranslation();
  const R = (
    label: string, pick: (m: MonthRow) => string, total: string,
    o?: { strong?: boolean; dim?: boolean },
  ) => (
    <MetricRow key={label} label={label} months={months} pick={pick} total={total}
      strong={o?.strong} dim={o?.dim} />
  );
  return (
    <Table minWidth={1120}>
      <thead>
        <tr>
          <th style={{ ...TH, ...STICKY }}>{t('ak_bud_metric')}</th>
          {months.map((m) => <th key={m.month} style={TH}>{monthShort(m.month)}</th>)}
          <th style={{ ...TH, color: 'var(--text)' }}>{t('ak_bud_year')}</th>
        </tr>
      </thead>
      <tbody>
        {R(t('ak_bud_grid_export_mwh'), (m) => n0(m.exportMwh), n0(year.exportMwh))}
        {R(t('ak_bud_average_sale_price_uah_mwh'), (m) => n0(m.avgPrice), n0(year.avgPrice))}
        {R(t('ak_bud_revenue_uah_mn'), (m) => mln(m.revenue), mln(year.revenue), { strong: true })}
        {R(t('ak_bud_incl_bess_effect'), (m) => mln(m.bessEffect), mln(year.bessEffect), { dim: true })}
        {R(t('ak_bud_variable_costs_uah_mn'), (m) => mln(m.variable), mln(year.variable))}
        {R(t('ak_bud_fixed_opex_uah_mn'), (m) => mln(m.opexFixed), mln(year.opexFixed))}
        {R(t('ak_bud_ebitda_uah_mn'), (m) => mln(m.ebitda), mln(year.ebitda), { strong: true })}
        {R(t('ak_bud_finance_costs_uah_mn'), (m) => mln(m.finance), mln(year.finance))}
        {R(t('ak_bud_pre_tax_profit_uah_mn'), (m) => mln(m.profitPreTax), mln(year.profitPreTax))}
        {R(t('ak_bud_income_tax_uah_mn'), (m) => mln(m.tax), mln(year.tax))}
        {R(t('ak_bud_net_profit_uah_mn'), (m) => mln(m.netProfit), mln(year.netProfit), { strong: true })}
        {R(t('ak_bud_loan_principal_repayment_uah_mn'), (m) => mln(m.principal), mln(year.principal))}
        {R(t('ak_bud_cash_flow_after_repayment_uah_mn'), (m) => mln(m.cashFlow), mln(year.cashFlow), { strong: true })}
        {R(t('ak_bud_cumulative_uah_mn'), (m) => mln(m.cashFlowCum), mln(year.cashFlow), { dim: true })}
      </tbody>
    </Table>
  );
}

export function BreakevenTable({ months, dscr }: { months: MonthRow[]; dscr: DscrRow[] }) {
  const { t } = useTranslation();
  return (
    <>
      <Table minWidth={760}>
        <thead>
          <tr>
            <th style={{ ...TH, ...STICKY }}>{t('ak_bud_month')}</th>
            <th style={TH}>{t('ak_bud_fixed_mn')}</th>
            <th style={TH}>{t('ak_bud_break_even_revenue_mn')}</th>
            <th style={TH}>{t('ak_bud_revenue_plan_mn')}</th>
            <th style={TH}>{t('ak_bud_safety_margin')}</th>
            <th style={TH}>{t('ak_bud_break_even_price_base')}</th>
            <th style={TH}>{t('ak_bud_break_even_export_mwh')}</th>
            <th style={TH}>{t('ak_bud_cash_break_even_mn')}</th>
          </tr>
        </thead>
        <tbody>
          {months.map((m) => (
            <tr key={m.month}>
              <td style={{ ...TD, ...STICKY }}>{monthShort(m.month)}</td>
              <td style={TD}>{mln(m.fixedCosts)}</td>
              <td style={TD}>{mln(m.beRevenue)}</td>
              <td style={TD}>{mln(m.revenue)}</td>
              {/* Запас прочності — позитивний показник, тож зелений; від'ємного
                  тут не буває, бо план виручки всюди вище БУ. */}
              <td style={{ ...TD, color: 'var(--ok)' }}>{pct(m.safetyMargin)}</td>
              <td style={TD}>{n0(m.beBasePrice)}</td>
              <td style={TD}>{n0(m.beExportMwh)}</td>
              {/* Кассова БУ у квартали з тілом кредиту вище плану — це не
                  помилка, а відома властивість графіка: покривається
                  накопиченим потоком. Тому бурштин, не червоний. */}
              <td style={{ ...TD, color: (m.cashMargin ?? 0) < 0 ? 'var(--warn)' : 'var(--text)' }}>
                {mln(m.beRevenueCash)}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      {dscr.length > 0 && (
        <div className="mt-4 pt-3" style={{ borderTop: '1px solid var(--line)' }}>
          <div className="text-[11px] mb-2" style={{ color: 'var(--text-muted)' }}>
            {t('ak_bud_debt_service_coverage_dscr_ebitda_inte')}
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {dscr.map((d) => (
              <div key={d.period} className="flex items-baseline gap-2">
                <span className="text-[11.5px]" style={{ color: 'var(--text-muted)' }}>{d.period}</span>
                <span className="font-display text-[15px]"
                  style={{ color: (d.dscr ?? 0) >= 1.5 ? 'var(--ok)' : 'var(--warn)' }}>
                  {n2(d.dscr)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

export function LoanTable({ loan }: { loan: LoanRow[] }) {
  const { t } = useTranslation();
  const sum = (f: keyof LoanRow) => loan.reduce((s, r) => s + ((r[f] as number) || 0), 0);
  return (
    <Table minWidth={900}>
      <thead>
        <tr>
          <th style={{ ...TH, ...STICKY }}>{t('ak_bud_month')}</th>
          <th style={TH}>{t('ak_bud_balance_eur')}</th>
          <th style={TH}>{t('ak_bud_fx_rate')}</th>
          <th style={TH}>{t('ak_bud_interest_mn')}</th>
          <th style={TH}>{t('ak_bud_principal_eur')}</th>
          <th style={TH}>{t('ak_bud_principal_uah_mn')}</th>
          <th style={TH}>{t('ak_bud_fee_k')}</th>
          <th style={TH}>{t('ak_bud_account_servicing_k')}</th>
          <th style={TH}>{t('ak_bud_debt_service_mn')}</th>
        </tr>
      </thead>
      <tbody>
        {loan.map((r) => (
          <tr key={r.month}>
            <td style={{ ...TD, ...STICKY }}>{monthShort(r.month)}</td>
            <td style={TD}>{n0(r.openEur)}</td>
            <td style={TD}>{n2(r.fxRate)}</td>
            <td style={TD}>{mln(r.interestUah)}</td>
            <td style={TD}>{n0(r.principalEur)}</td>
            <td style={TD}>{mln(r.principalUah)}</td>
            <td style={TD}>{n0(r.commission == null ? null : r.commission / 1000)}</td>
            <td style={TD}>{n0(r.rko == null ? null : r.rko / 1000)}</td>
            <td style={{ ...TD, fontWeight: 600 }}>{mln(r.debtService)}</td>
          </tr>
        ))}
        <tr>
          <td style={{ ...TD, ...STICKY, fontWeight: 600 }}>{t('ak_bud_year')}</td>
          <td style={TD} /><td style={TD} />
          <td style={{ ...TD, fontWeight: 600 }}>{mln(sum('interestUah'))}</td>
          <td style={{ ...TD, fontWeight: 600 }}>{n0(sum('principalEur'))}</td>
          <td style={{ ...TD, fontWeight: 600 }}>{mln(sum('principalUah'))}</td>
          <td style={{ ...TD, fontWeight: 600 }}>{n0(sum('commission') / 1000)}</td>
          <td style={{ ...TD, fontWeight: 600 }}>{n0(sum('rko') / 1000)}</td>
          <td style={{ ...TD, fontWeight: 600 }}>{mln(sum('debtService'))}</td>
        </tr>
      </tbody>
    </Table>
  );
}

export function ParamsPanel({ params, opex, byScenario }: {
  params: ParamRow[]; opex: OpexRow[]; byScenario: ScenarioYearRow[];
}) {
  const { t } = useTranslation();
  const fmtParam = (p: ParamRow) => {
    if (p.valueText) return p.valueText;
    if (p.valueNum == null) return '—';
    const v = p.valueNum;
    // Частки до одиниці — відсотками; великі суми — з розрядами.
    if (v > 0 && v < 1) return pct(v, v < 0.01 ? 2 : 1);
    return n0(v);
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-5">
      <div>
        <div className="text-[11px] mb-2" style={{ color: 'var(--text-muted)' }}>
          {t('ak_bud_version_assumptions')}
        </div>
        {params.map((p) => (
          <div key={p.ord} className="flex items-baseline justify-between gap-3 py-1.5"
            style={{ borderTop: '1px solid var(--line)' }}>
            <span className="text-[12px] min-w-0" style={{ color: 'var(--text)' }}>
              {p.key}
              {p.note && (
                <span className="block text-[10.5px] mt-0.5" style={{ color: 'var(--text-dim)' }}>{p.note}</span>
              )}
            </span>
            <span className="font-display text-[13px] shrink-0" style={{ color: 'var(--text)' }}>
              {fmtParam(p)}
            </span>
          </div>
        ))}
      </div>
      <div>
        <div className="text-[11px] mb-2" style={{ color: 'var(--text-muted)' }}>
          {t('ak_bud_fixed_opex_items_uah_month')}
        </div>
        {opex.map((o) => (
          <div key={o.code} className="flex items-baseline justify-between gap-3 py-1.5"
            style={{ borderTop: '1px solid var(--line)' }}>
            <span className="text-[12px]" style={{ color: 'var(--text)' }}>{o.name}</span>
            <span className="font-display text-[13px] shrink-0" style={{ color: 'var(--text)' }}>
              {n0(o.amountMonth)}
            </span>
          </div>
        ))}
        {opex[0]?.indexation != null && (
          <div className="text-[11px] mt-2" style={{ color: 'var(--text-dim)' }}>
            {t('ak_bud_indexation_from', { p: pct(opex[0].indexation, 0), d: opex[0].indexationFrom?.slice(0, 10).split('-').reverse().join('.') })}
          </div>
        )}

        {byScenario.length > 0 && (
          <>
            <div className="text-[11px] mt-5 mb-2" style={{ color: 'var(--text-muted)' }}>
              {t('ak_bud_year_by_price_scenario_uah_mn')}
            </div>
            <Table minWidth={320}>
              <thead>
                <tr>
                  <th style={{ ...TH, ...STICKY }}>{t('ak_bud_metric')}</th>
                  <th style={TH}>{t('ak_bud_low')}</th>
                  <th style={TH}>{t('ak_bud_base')}</th>
                  <th style={TH}>{t('ak_bud_high')}</th>
                </tr>
              </thead>
              <tbody>
                {byScenario.map((r) => (
                  <tr key={r.metric}>
                    <td style={{ ...TD, ...STICKY, fontSize: 11.5 }}>
                      {r.metric.replace(/, грн$/, '').replace(/ \(.*\)/, '')}
                    </td>
                    <td style={TD}>{mln(r.low, 1)}</td>
                    <td style={{ ...TD, fontWeight: 600 }}>{mln(r.base, 1)}</td>
                    <td style={TD}>{mln(r.high, 1)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </div>
    </div>
  );
}
